"use client";

import { throttle } from "lodash";
import { useLayoutEffect, useRef, useState } from "react";

type UseContainerWidth = <T extends HTMLElement = HTMLDivElement>(options?: {
  throttleTime?: number;
}) => {
  width: number;
  containerRef: React.RefObject<T | null>;
};

export const useContainerWidth: UseContainerWidth = <T extends HTMLElement = HTMLDivElement>({
  throttleTime = 100,
}: {
  throttleTime?: number;
} = {}) => {
  const containerRef = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateWidth = throttle(() => {
      setWidth(element.offsetWidth);
    }, throttleTime);

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    updateWidth();

    return () => {
      resizeObserver.disconnect();
      updateWidth.cancel();
    };
  }, [throttleTime]);

  return { width, containerRef };
};
